/* ==========================================================================
   Espera e horários do Atendimento (design D13, D18)

   A espera ("há 4 min") é contada do início do alerta contra o relógio do
   servidor (`serverTime` do resumo), não o do navegador: um computador da
   loja com o relógio errado não pode pintar de vermelho quem acabou de
   chegar. Acima de 5 minutos a espera fica em vermelho. Os horários ("às
   18:00") são do fuso da loja.
   ========================================================================== */

/** Acima disto a espera fica em vermelho. */
export const LONG_WAIT_MS = 5 * 60_000;

const TIME_ZONE = 'America/Sao_Paulo';

const clockFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export interface ServerClock {
  /** O `serverTime` da última leitura do resumo. */
  serverTime: string;
  /** Quando o navegador recebeu essa leitura (`Date.now()`). */
  receivedAt: number;
}

/** Agora, no relógio do servidor: o `serverTime` mais o que passou no navegador desde a leitura. */
export function serverNow(clock: ServerClock | null | undefined, browserNow = Date.now()): number {
  if (!clock) return browserNow;
  const server = Date.parse(clock.serverTime);
  if (Number.isNaN(server)) return browserNow;
  return server + Math.max(0, browserNow - clock.receivedAt);
}

/** Milissegundos desde `since`, nunca negativo. */
export function elapsedMs(since: string | null | undefined, now: number): number {
  if (!since) return 0;
  const start = Date.parse(since);
  return Number.isNaN(start) ? 0 : Math.max(0, now - start);
}

/** "há 4 min", "há 1 h 05 min" (menos de um minuto: "agora"). */
export function formatWait(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `há ${hours} h` : `há ${hours} h ${String(rest).padStart(2, '0')} min`;
}

export interface WaitTime {
  text: string;
  /** Acima de 5 minutos: em vermelho. */
  long: boolean;
}

/** A espera de uma conversa alertada desde `alertedAt`, contada contra `now` (o relógio do servidor). */
export function waitTime(alertedAt: string | null | undefined, now: number): WaitTime | null {
  if (!alertedAt) return null;
  const ms = elapsedMs(alertedAt, now);
  return { text: formatWait(ms), long: ms > LONG_WAIT_MS };
}

/** "18:00", no fuso da loja. Vazio quando a data não é válida. */
export function formatClock(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : clockFormatter.format(date);
}

/** O dia no fuso da loja, como "2026-10-18" (o `en-CA` escreve ano, mês e dia nessa ordem). */
const dayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const dayMonthFormatter = new Intl.DateTimeFormat('pt-BR', { timeZone: TIME_ZONE, day: '2-digit', month: '2-digit' });

/**
 * O horário de uma mensagem da conversa, no fuso da loja: "18:00" no dia de
 * hoje, "ontem 18:00" e "16/10 18:00" antes disso, para a conversa que passa
 * de um dia não ficar com horários ambíguos.
 */
export function formatMessageTime(iso: string | null | undefined, now: number = Date.now()): string {
  const clock = formatClock(iso);
  if (!clock) return '';
  const date = new Date(iso!);
  const day = dayFormatter.format(date);
  if (day === dayFormatter.format(new Date(now))) return clock;
  // Meio-dia de hoje menos 24 h cai sempre no dia anterior, mesmo com horário de verão.
  const todayNoon = Date.parse(`${dayFormatter.format(new Date(now))}T12:00:00-03:00`);
  if (day === dayFormatter.format(new Date(todayNoon - 24 * 60 * 60_000))) return `ontem ${clock}`;
  return `${dayMonthFormatter.format(date)} ${clock}`;
}

/**
 * A espera numa frase ("há 3 min", "há 1 h 05 min"), para a faixa e o card:
 * abaixo de um minuto, "há menos de 1 min" (o "agora" da lista não cabe em
 * "o mais antigo agora").
 */
export function formatWaitPhrase(ms: number): string {
  return ms < 60_000 ? 'há menos de 1 min' : formatWait(ms);
}
